"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Image } from "lucide-react";

export function CombineStacksModal({ isOpen, onClose, stack1, stack2, onConfirm }) {
  const [selectedOption, setSelectedOption] = useState(null); // 'stack1', 'stack2', or 'new'
  const [newStackName, setNewStackName] = useState("Combined Stack");
  const [newStackImage, setNewStackImage] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOption(null);
      setNewStackName("Combined Stack");
      setNewStackImage(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedOption === 'stack1') {
      onConfirm(stack1);
    } else if (selectedOption === 'stack2') {
      onConfirm(stack2);
    } else if (selectedOption === 'new') {
      // Create a new stack object with custom name and image
      onConfirm({
        id: stack1.id, // Keep the first stack's ID
        name: newStackName,
        image: newStackImage,
        projects: [...stack1.projects, ...stack2.projects],
        projectIds: [...stack1.projects.map(p => p.id), ...stack2.projects.map(p => p.id)],
        createdAt: stack1.createdAt
      });
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedOption(null);
    setNewStackName("Combined Stack");
    setNewStackImage(null);
    onClose();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewStackImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="!max-w-4xl w-[90vw] p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-2">Combine Stacks</h2>
            <p className="text-sm text-gray-600">
              Select which stack's name and image to keep for the combined stack
            </p>
          </div>

          {/* Stack Options */}
          <div className="grid grid-cols-3 gap-4">
            {/* Stack 1 Option */}
            <button
              onClick={() => setSelectedOption('stack1')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedOption === 'stack1'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {/* Stack Image */}
                {stack1?.image ? (
                  <img
                    src={stack1.image}
                    alt={stack1.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 grid grid-cols-2 gap-1">
                    <div className="bg-green-500 rounded-sm"></div>
                    <div className="bg-red-500 rounded-sm"></div>
                    <div className="bg-green-400 rounded-sm"></div>
                    <div className="bg-gray-400 rounded-sm"></div>
                  </div>
                )}

                {/* Stack Name */}
                <div className="text-center">
                  <h3 className="font-semibold text-lg">{stack1?.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {stack1?.projects?.length || 0} space{stack1?.projects?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </button>

            {/* Stack 2 Option */}
            <button
              onClick={() => setSelectedOption('stack2')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedOption === 'stack2'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {/* Stack Image */}
                {stack2?.image ? (
                  <img
                    src={stack2.image}
                    alt={stack2.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 grid grid-cols-2 gap-1">
                    <div className="bg-green-500 rounded-sm"></div>
                    <div className="bg-red-500 rounded-sm"></div>
                    <div className="bg-green-400 rounded-sm"></div>
                    <div className="bg-gray-400 rounded-sm"></div>
                  </div>
                )}

                {/* Stack Name */}
                <div className="text-center">
                  <h3 className="font-semibold text-lg">{stack2?.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {stack2?.projects?.length || 0} space{stack2?.projects?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </button>

            {/* New Stack Option */}
            <button
              onClick={() => setSelectedOption('new')}
              className={`p-4 rounded-lg border-2 border-dashed transition-all ${
                selectedOption === 'new'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {/* Image Upload */}
                <div className="relative">
                  <input
                    type="file"
                    id="new-stack-image"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label
                    htmlFor="new-stack-image"
                    className="cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {newStackImage ? (
                      <img
                        src={newStackImage}
                        alt="New stack"
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                        <Image className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </label>
                </div>

                {/* Stack Name Input */}
                <div className="text-center w-full">
                  <Input
                    value={newStackName}
                    onChange={(e) => setNewStackName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="text-center font-semibold text-sm h-8"
                    placeholder="Stack name"
                  />
                  <p className="text-xs text-gray-500 mt-1">New Stack</p>
                </div>
              </div>
            </button>
          </div>

          {/* Info Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {selectedOption === 'stack1'
                ? `All ${(stack1?.projects?.length || 0) + (stack2?.projects?.length || 0)} spaces will be combined into "${stack1?.name}"`
                : selectedOption === 'stack2'
                ? `All ${(stack1?.projects?.length || 0) + (stack2?.projects?.length || 0)} spaces will be combined into "${stack2?.name}"`
                : selectedOption === 'new'
                ? `All ${(stack1?.projects?.length || 0) + (stack2?.projects?.length || 0)} spaces will be combined into "${newStackName}"`
                : 'Select an option above to continue'
              }
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!selectedOption}>
              Combine Stacks
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
